import { useState } from "react";
import { Checkbox, Select, SettingsPanel } from "../../shared";
import { SettingsHeader } from ".";
import { useConfig } from "../../hooks/useConfig";
import { DRIVER_ASSIGNMENT_ENFORCEMENT_OPTIONS } from "../../constants/driver-profile";

const WARNING_DAY_OPTIONS = [7, 14, 30, 60, 90];

/**
 * Trucking driver compliance settings — assignment enforcement and expiry windows.
 */
const DriversSettings = () => {
  const { addConfig, configs, appType } = useConfig();
  const [saving, setSaving] = useState(false);

  const compliance = configs?.features?.drivers?.compliance || {};

  if (appType !== "trucking") {
    return (
      <div>
        <SettingsHeader title="Drivers" />
        <p className="text-sm text-gray-600">
          Driver compliance settings are available for trucking organizations only.
        </p>
      </div>
    );
  }

  async function patchDrivers(partial) {
    setSaving(true);
    try {
      await addConfig({
        features: {
          ...configs?.features,
          drivers: {
            ...configs?.features?.drivers,
            compliance: {
              ...compliance,
              ...partial,
            },
          },
        },
      });
    } finally {
      setSaving(false);
    }
  }

  const panelData = [
    {
      title: "Enable driver compliance checks",
      subtitle:
        "When enabled, CDL, medical cert, and hazmat expiry are evaluated on driver reads and assignment.",
      content: (
        <Checkbox
          id="drivers-compliance-enabled"
          checked={compliance.enabled !== false}
          disabled={saving}
          label="Enable compliance tracking"
          onChange={(target) =>
            patchDrivers({
              enabled: target.checked,
            })
          }
        />
      ),
    },
    {
      title: "Expiring soon window (days)",
      subtitle:
        "Credentials within this many days of expiry are flagged as expiring soon in lists and assignment warnings.",
      content: (
        <Select
          id="drivers-warning-days"
          size="sm"
          disabled={saving || compliance.enabled === false}
          value={String(compliance.warningDays ?? 30)}
          options={WARNING_DAY_OPTIONS.map((day) => ({
            label: `${day} days`,
            value: String(day),
          }))}
          onChange={(e) =>
            patchDrivers({
              warningDays: Number(e.value),
            })
          }
        />
      ),
    },
    {
      title: "Assignment enforcement",
      subtitle:
        "Controls leg and dispatch assignment when a driver has expired or missing credentials.",
      content: (
        <Select
          id="drivers-assignment-enforcement"
          size="sm"
          disabled={saving || compliance.enabled === false}
          value={compliance.assignmentEnforcement || "warn"}
          options={DRIVER_ASSIGNMENT_ENFORCEMENT_OPTIONS}
          onChange={(e) =>
            patchDrivers({
              assignmentEnforcement: e.value,
            })
          }
        />
      ),
    },
    {
      title: "Hazmat load gate",
      subtitle:
        "When enabled, hazmat manifests require a driver with valid hazmat certification.",
      content: (
        <Checkbox
          id="drivers-hazmat-gate"
          checked={compliance.checkHazmatOnHazmatLoads !== false}
          disabled={saving || compliance.enabled === false}
          label="Require hazmat cert on hazmat loads"
          onChange={(target) =>
            patchDrivers({
              checkHazmatOnHazmatLoads: target.checked,
            })
          }
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <SettingsHeader title="Drivers" />
      <p className="text-sm text-gray-600">
        Configure driver qualification compliance checks, warning windows, and assignment rules.
      </p>
      <SettingsPanel title="Compliance" data={panelData} />
    </div>
  );
};

export default DriversSettings;
